import {useParams} from "react-router-dom"
import {Result} from "../../client/Result"
import {CrudForm, FieldProp, MultiFieldProp} from "../common/CrudForm"
import {BuildingClient, Programme} from "../../client/BuildingClient"
import { useState} from "react"
import {SelectOption} from "../common/MuiSelect"
import {meteringOptionalRoute} from "../../Routes"
import {AsyncComponentP} from "../common/AsyncComponent";

type InitialData = { currentProgramme: Programme }

interface EditProgrammePageProps {
    buildingClient: BuildingClient
    buildingId: number
    initialData: InitialData
}

interface ProgrammeFrom {
    programme: SelectOption
    minimumPriceInEurosPerMegawattHour: number
    participatesInCapacityBidding: boolean
}

interface AsyncAddProgrammePageProp{
    buildingClient: BuildingClient
}

export type EditProgrammeParams = {
    buildingId: string
    programmeName: string
}

export const AsyncEditProgrammePage= ({ buildingClient }: AsyncAddProgrammePageProp) =>{
    const { buildingId, programmeName } = useParams<EditProgrammeParams>()

    async function load() : Promise<Result<InitialData>> {
        const h = await buildingClient.retrieveBuildingProgrammeDetails(Number(buildingId), String(programmeName))

        return h.map( programme => ({currentProgramme: programme}))}

    return <AsyncComponentP loaderFunction={load} dataPropName={'initialData'}>
        <EditProgrammePage buildingClient={buildingClient} buildingId={Number(buildingId)} initialData={{currentProgramme: {name:''}}}/>
    </AsyncComponentP>
}

export function toProgrammeFrom(p: Programme): ProgrammeFrom {
    return {
        programme: { label: p.name, id: p.name },
        minimumPriceInEurosPerMegawattHour: p.minimumPriceInEurosPerMegawattHour ?? 0,
        participatesInCapacityBidding: p.participatesInCapacityBidding ?? false,
    }
}

const EditProgrammePage = ({ buildingClient, buildingId, initialData}: EditProgrammePageProps) => {

    const [ currentProgrammeForm , setCurrentProgramme] = useState<ProgrammeFrom>(toProgrammeFrom(initialData.currentProgramme))


    function onChange(newState: Partial<ProgrammeFrom>): void {
        setCurrentProgramme((prev) => ({ ...prev, ...newState }))
    }

    function handleSubmit(programmeForm: Partial<ProgrammeFrom>): Promise<Result<null>> {
        const {programme, minimumPriceInEurosPerMegawattHour, participatesInCapacityBidding} = programmeForm
        if (programme) {
            const price = minimumPriceInEurosPerMegawattHour ? parseInt(String(minimumPriceInEurosPerMegawattHour)) : undefined
            return buildingClient.editBuildingProgrammeDetails(buildingId,{name: programme.label, minimumPriceInEurosPerMegawattHour: price, participatesInCapacityBidding: participatesInCapacityBidding})
        } else {
            throw new Error('Submit must have a programme value')
        }
    }

    return  <CrudForm redirectPath={meteringOptionalRoute.withParams(buildingId)} fields={formFields(true, optionalFields(initialData.currentProgramme.name),[currentProgrammeForm.programme])} onSubmit={handleSubmit} onChange={onChange} data={currentProgrammeForm}/>
}

export const optionalFields=(selectedName): (FieldProp<ProgrammeFrom> | MultiFieldProp<ProgrammeFrom>)[] => selectedName === 'mFRR' ?
    [
        {key: `minimumPriceInEurosPerMegawattHour`, label: 'Minimum Price in €/MWh', type: 'number', required: true},
        {key: `participatesInCapacityBidding`, label: 'Participates in Capacity Bidding', type: 'boolean', required: true}
    ] :
    []

export const formFields=(disableProgrammeEdit: boolean, optionalFields: (FieldProp<ProgrammeFrom> | MultiFieldProp<ProgrammeFrom>)[], programme: SelectOption[]): (FieldProp<ProgrammeFrom> | MultiFieldProp<ProgrammeFrom>)[] => [
    { key: `programme`, label: 'Programme', type: 'select', options: programme, required: true, disabled:disableProgrammeEdit },
    ...optionalFields
]
